import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FloppyDisk, ArrowsClockwise, Table, Warning, CheckCircle } from "@phosphor-icons/react";

// Reference/lookup targets need a Salesforce record Id, not a phone/text value.
const REF_TYPES = new Set(["reference", "id"]);
const REF_KEYS = new Set(["contact_id", "account_id"]);

// Flag mapped fields that Salesforce would reject or silently drop:
//  - "missing": the target isn't a writeable field on the object (bad API name / not createable)
//  - "type":    a lookup/Id field mapped from a non-Id detail (e.g. To Number → OwnerId)
function computeMappingIssues(map, fields, labelOf) {
    if (!fields || fields.length === 0) return [];
    const byName = new Map(fields.map((o) => [o.name, o]));
    const issues = [];
    for (const [key, sfName] of Object.entries(map || {})) {
        if (!sfName) continue;
        const meta = byName.get(sfName);
        if (!meta) {
            issues.push({ key, sfName, kind: "missing", label: labelOf(key) });
        } else if (REF_TYPES.has((meta.type || "").toLowerCase()) && !REF_KEYS.has(key)) {
            issues.push({ key, sfName, kind: "type", type: meta.type, label: labelOf(key) });
        }
    }
    return issues;
}

function MappingHealth({ issues, ready, testid }) {
    if (!ready) return null;
    if (issues.length === 0) {
        return (
            <span data-testid={`${testid}-ok`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                <CheckCircle size={12} weight="fill" /> Mapping valid
            </span>
        );
    }
    return (
        <div data-testid={`${testid}-warn`} className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="flex items-center gap-1.5 font-bold"><Warning size={13} weight="fill" /> {issues.length} mapping issue{issues.length > 1 ? "s" : ""} — these won't save to Salesforce</div>
            <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {issues.map((i) => (
                    <li key={i.key}>
                        <b>{i.label}</b> → <code>{i.sfName}</code>{" "}
                        {i.kind === "missing"
                            ? "isn't a writeable field on this object (check the API name)."
                            : `is a lookup/Id field (${i.type}); it needs a Salesforce record Id, not this value.`}
                    </li>
                ))}
            </ul>
        </div>
    );
}

// Admin maps Inspiro's logical call fields -> the customer's Salesforce field API names
// (standard Task fields and/or a custom object). Dropdowns are populated live from the
// Salesforce Describe API. Backend then populates these on every inbound & outbound call.
export default function SalesforceFieldMapping({ sf, onSaved }) {
    const [catalog, setCatalog] = useState([]);
    const [taskFields, setTaskFields] = useState([]);
    const [objectFields, setObjectFields] = useState([]);
    const [taskMap, setTaskMap] = useState({});
    const [objectMap, setObjectMap] = useState({});
    const [callObject, setCallObject] = useState("");
    const [logToObject, setLogToObject] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingObj, setLoadingObj] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        setTaskMap(sf?.task_map || {});
        setObjectMap(sf?.object_map || {});
        setCallObject(sf?.call_object || "");
        setLogToObject(!!sf?.log_to_object);
    }, [sf]);

    useEffect(() => {
        if (!sf?.connected) return;
        (async () => {
            try {
                const [cat, tf] = await Promise.all([
                    api.get("/integrations/salesforce/field-catalog"),
                    api.get("/integrations/salesforce/describe/Task"),
                ]);
                setCatalog(cat.data.fields || []);
                setTaskFields(tf.data.fields || []);
            } catch (e) { /* not connected yet */ }
        })();
    }, [sf?.connected]);

    const loadObjectFields = async (silent = false) => {
        const name = callObject.trim();
        if (!name) return;
        setLoadingObj(true);
        try {
            const { data } = await api.get(`/integrations/salesforce/describe/${name}`);
            setObjectFields(data.fields || []);
            if (!silent) toast.success(`Loaded ${data.fields?.length || 0} fields from ${name}`);
        } catch (e) {
            if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Could not load object fields");
        } finally { setLoadingObj(false); }
    };

    // Auto-load the saved custom object's fields so the mapping health check can
    // flag issues immediately (no manual "Load fields" click needed).
    useEffect(() => {
        if (sf?.connected && sf?.log_to_object && (sf?.call_object || "").trim() && objectFields.length === 0) {
            loadObjectFields(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sf?.connected, sf?.log_to_object, sf?.call_object]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/integrations/salesforce/field-map", {
                task_map: taskMap, object_map: objectMap, call_object: callObject.trim(), log_to_object: logToObject,
            });
            toast.success("Field mapping saved");
            onSaved?.(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save mapping");
        } finally { setSaving(false); }
    };

    // Re-read the connected org's Task fields (bypassing the server cache) and
    // re-validate the saved mapping — use after reconnecting a new Salesforce org.
    const refreshMapping = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/integrations/salesforce/field-map/refresh");
            setTaskFields(data.fields || []);
            const bad = Object.keys(data.invalid_task_fields || {}).length;
            if (bad === 0) toast.success("Fields refreshed — mapping is valid for this org");
            else toast.warning(`Fields refreshed — ${bad} mapped field${bad > 1 ? "s" : ""} not found in this org`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not refresh fields");
        } finally { setRefreshing(false); }
    };

    const labelOf = useMemo(() => {
        const m = new Map(catalog.map((f) => [f.key, f.label]));
        return (k) => m.get(k) || k;
    }, [catalog]);
    const taskIssues = useMemo(() => computeMappingIssues(taskMap, taskFields, labelOf), [taskMap, taskFields, labelOf]);
    const objectIssues = useMemo(() => computeMappingIssues(objectMap, objectFields, labelOf), [objectMap, objectFields, labelOf]);

    const FieldRow = ({ f, value, options, onChange, testid }) => {
        const known = !value || options.some((o) => o.name === value);
        return (
        <div className="grid grid-cols-2 items-center gap-3 py-1.5">
            <div className="text-sm">
                {f.label}
                {f.ai && <span className="ml-2 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-700">AI</span>}
                <span className="ml-1 text-[10px] text-stone2">({f.type})</span>
            </div>
            <select
                data-testid={testid}
                value={value || ""}
                onChange={(e) => onChange(f.key, e.target.value)}
                className={`w-full rounded-lg border bg-white px-2 py-1.5 text-xs ${known ? "border-stone-300" : "border-amber-400 text-amber-800"}`}
            >
                <option value="">— Not mapped —</option>
                {value && !known && (
                    <option value={value}>⚠ {value} — not on this object</option>
                )}
                {options.map((o) => (
                    <option key={o.name} value={o.name}>{o.label} ({o.name})</option>
                ))}
            </select>
        </div>
        );
    };

    if (!sf?.connected) {
        return <p className="text-xs text-stone2" data-testid="mapping-needs-connect">Connect Salesforce to configure field mapping.</p>;
    }

    return (
        <div data-testid="sf-field-mapping" className="space-y-6">
            {sf?.sf_last_error && (
                <div data-testid="sf-last-error" className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs text-red-900">
                    <div className="flex items-center gap-1.5 font-bold"><Warning size={14} weight="fill" /> Last Salesforce Task creation failed</div>
                    <p className="mt-1">Salesforce rejected the most recent call log. Fix the cause below (often an unmapped agent owner or a field that doesn't exist in this org), then place a test call.</p>
                    <code className="mt-2 block whitespace-pre-wrap break-words rounded bg-white/70 p-2 text-[11px]">{String(sf.sf_last_error).slice(0, 400)}</code>
                </div>
            )}
            <div>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-stone2"><Table size={14} /> Task field mapping</div>
                    <div className="flex items-center gap-2">
                        <MappingHealth issues={taskIssues} ready={taskFields.length > 0} testid="task-map-health" />
                        <button data-testid="refresh-field-map" onClick={refreshMapping} disabled={refreshing}
                            title="Re-read this Salesforce org's fields and re-validate the mapping (use after reconnecting a new org)"
                            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold hover:border-ink disabled:opacity-50">
                            <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh fields"}
                        </button>
                    </div>
                </div>
                <p className="mt-1 text-xs text-stone2">Map each call detail to a Salesforce <b>Task</b> field. Populated automatically on every inbound & outbound call. After reconnecting a new Salesforce org, click <b>Refresh fields</b> to re-validate against it.</p>
                <div className="mt-3 divide-y divide-stone-100 rounded-xl border border-stone-200 px-4 py-2">
                    {catalog.map((f) => (
                        <FieldRow key={f.key} f={f} value={taskMap[f.key]} options={taskFields}
                            onChange={(k, v) => setTaskMap((m) => ({ ...m, [k]: v }))}
                            testid={`task-map-${f.key}`} />
                    ))}
                </div>
            </div>

            <div className="rounded-xl border border-stone-200 p-4">
                <label className="flex items-center gap-2 text-sm font-semibold">
                    <input type="checkbox" data-testid="log-to-object-toggle" checked={logToObject}
                        onChange={(e) => setLogToObject(e.target.checked)} />
                    Also log calls to a custom object (for native reports & dashboards)
                </label>
                {logToObject && (
                    <>
                        <div className="mt-3 flex items-end gap-2">
                            <div className="flex-1">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Custom object API name</label>
                                <input data-testid="call-object-input" value={callObject}
                                    onChange={(e) => setCallObject(e.target.value)} placeholder="Inspiro_Call__c"
                                    className="mt-1 w-full rounded-lg border border-stone-300 px-2 py-1.5 text-xs" />
                            </div>
                            <button data-testid="load-object-fields" onClick={() => loadObjectFields()} disabled={loadingObj}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-xs font-semibold hover:border-ink">
                                <ArrowsClockwise size={13} /> {loadingObj ? "Loading…" : "Load fields"}
                            </button>
                        </div>
                        {objectFields.length > 0 && (
                            <div className="mt-3 divide-y divide-stone-100 rounded-xl border border-stone-200 px-4 py-2">
                                {catalog.map((f) => (
                                    <FieldRow key={f.key} f={f} value={objectMap[f.key]} options={objectFields}
                                        onChange={(k, v) => setObjectMap((m) => ({ ...m, [k]: v }))}
                                        testid={`object-map-${f.key}`} />
                                ))}
                            </div>
                        )}
                        <div className="mt-2"><MappingHealth issues={objectIssues} ready={objectFields.length > 0} testid="object-map-health" /></div>
                    </>
                )}
            </div>

            <button data-testid="save-field-map" onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform">
                <FloppyDisk size={16} /> {saving ? "Saving…" : "Save field mapping"}
            </button>
        </div>
    );
}
